import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    X,
    Download,
    Calendar,
    Camera,
    User,
    FileImage,
    MapPin,
    Settings,
    Eye,
    EyeOff
} from 'lucide-react';
import { WatermarkedImage } from "@/components/ui/watermarked-image";
import { PhotoService } from "@/photos/services/photoService";

interface Photo {
    id: string;
    filename: string;
    storageurl: string;
    thumbnailurl: string;
    resolution: string;
    processingstatus: string;
    visibility: string;
    author?: string;
    capture_datetime?: string;
    camera_model?: string;
    original_filename?: string;
    bibnumbers?: string;
    metadata: {
        cameraModel?: string;
        author?: string;
        originalName?: string;
        fileSize?: number;
        captureDateTime?: string;
        gpsLatitude?: number;
        gpsLongitude?: number;
        technical?: any;
    };
    uploadedat: string;
    createdat: string;
    batchid?: string;
}

interface PhotoPreviewModalProps {
    photo: Photo | null;
    isOpen: boolean;
    onClose: () => void;
    onDownload?: (photo: Photo) => void;
}

export const PhotoPreviewModal: React.FC<PhotoPreviewModalProps> = ({
    photo,
    isOpen,
    onClose,
    onDownload
}) => {
    const [showMetadata, setShowMetadata] = React.useState(false);
    const [imageError, setImageError] = React.useState(false);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDownload = () => {
        if (photo && onDownload) {
            onDownload(photo);
        }
    };

    const handleImageError = () => {
        setImageError(true);
    };

    if (!isOpen || !photo) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-7xl max-h-full w-full h-full overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold">Visualização da Foto</h2>
                        <p className="text-sm text-gray-600">
                            {photo.metadata?.originalName || photo.filename}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowMetadata(!showMetadata)}
                            className="flex items-center gap-2"
                        >
                            {showMetadata ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            <span>{showMetadata ? 'Ocultar' : 'Mostrar'} Metadados</span>
                        </Button>

                        {onDownload && (
                            <Button
                                size="sm"
                                onClick={handleDownload}
                                className="flex items-center gap-2"
                            >
                                <Download className="h-4 w-4" />
                                <span>Download</span>
                            </Button>
                        )}

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" />
                            Fechar
                        </Button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                    <div className="h-full grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
                        {/* Imagem Principal */}
                        <div className="lg:col-span-2">
                            <Card className="h-full">
                                <CardContent className="p-0 h-full">
                                    <div className="h-full bg-muted rounded-lg overflow-hidden flex items-center justify-center">
                                        {imageError ? (
                                            <div className="text-center">
                                                <FileImage className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                                <p className="text-muted-foreground">Erro ao carregar a imagem</p>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setImageError(false)}
                                                    className="mt-2"
                                                >
                                                    Tentar Novamente
                                                </Button>
                                            </div>
                                        ) : (
                                            <WatermarkedImage
                                                src={photo.storageurl}
                                                alt={photo.metadata?.originalName || photo.filename}
                                                className="max-w-full max-h-full object-contain"
                                                watermark={PhotoService.shouldWatermark(photo)}
                                                variant="full"
                                                onError={handleImageError}
                                            />
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Informações da Foto */}
                        {showMetadata && (
                            <div className="space-y-4 overflow-y-auto">
                                {/* Informações Básicas */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center space-x-2">
                                            <FileImage className="h-5 w-5" />
                                            <span>Informações</span>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <Camera className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">Câmera:</span>
                                            <span className="text-sm">{photo.camera_model || '-'}</span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <User className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">Autor:</span>
                                            <span className="text-sm">{photo.author || '-'}</span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <Calendar className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">Capturada:</span>
                                            <span className="text-sm">
                                                {photo.capture_datetime ? formatDate(photo.capture_datetime) : '-'}
                                            </span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <Calendar className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">Upload:</span>
                                            <span className="text-sm">{formatDate(photo.uploadedat)}</span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium">Resolução:</span>
                                            <Badge variant="outline" className="text-xs">
                                                {photo.resolution}
                                            </Badge>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium">Tamanho:</span>
                                            <span className="text-sm">
                                                {photo.metadata?.technical?.file_size_bytes ? formatFileSize(photo.metadata.technical.file_size_bytes) : '-'}
                                            </span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium">Arquivo Original:</span>
                                            <span className="text-sm">{photo.original_filename || photo.filename}</span>
                                        </div>

                                        {photo.bibnumbers && (
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium">Numero do Peito:</span>
                                                <span className="text-sm">{photo.bibnumbers}</span>
                                            </div>
                                        )}

                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium">Status:</span>
                                            <Badge
                                                variant={photo.processingstatus === 'Ready' ? 'default' : 'secondary'}
                                                className="text-xs"
                                            >
                                                {photo.processingstatus}
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Metadados Técnicos */}
                                {photo.metadata?.technical && (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="flex items-center space-x-2">
                                                <Settings className="h-5 w-5" />
                                                <span>Metadados Técnicos</span>
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2 text-sm max-h-40 overflow-y-auto">
                                                {Object.entries(photo.metadata.technical).map(([key, value]) => (
                                                    <div key={key} className="flex justify-between">
                                                        <span className="text-muted-foreground capitalize">
                                                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                                                        </span>
                                                        <span className="font-medium">{String(value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Localização GPS */}
                                {photo.metadata?.gpsLatitude && photo.metadata?.gpsLongitude && (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="flex items-center space-x-2">
                                                <MapPin className="h-5 w-5" />
                                                <span>Localização</span>
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Latitude:</span>
                                                    <span className="font-medium">{photo.metadata.gpsLatitude}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Longitude:</span>
                                                    <span className="font-medium">{photo.metadata.gpsLongitude}</span>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full mt-2"
                                                    onClick={() => {
                                                        const url = `https://www.google.com/maps?q=${photo.metadata.gpsLatitude},${photo.metadata.gpsLongitude}`;
                                                        window.open(url, '_blank');
                                                    }}
                                                >
                                                    Ver no Google Maps
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
